import { PageController } from '@client/components/_core/layout/PageController';
import { Button } from '@client/components/_ui/data-entry/ButtonModern';
import { InlineQueryError } from '@client/components/_ui/feedback/InlineQueryError';
import { createColumnHelper, PrismaTable, usePrismaTable } from '@client/hooks/table-v2';
import { useFormatter } from '@client/hooks/utils/useFormatter';
import type { RouterInputs, RouterOutputs } from '@client/trpc/client';
import { trpc } from '@client/trpc/client';
import { skipToken } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { Input, Select } from 'antd';
import { useState } from 'react';
import { VehicleDetailsDrawer } from '../VehicleDetailsDrawer';
import { isConsolidatorRecord } from './ClientRecordHeader';
import { useCanManageFleetClients, useClientRecord } from './client-record-data';
import { EmptyValue, RecordCard, ValueChip } from './record-primitives';

export const Route = createFileRoute('/console/$orgId/configuration/fleet/client-database/details/$id/vehicles')({
    component: RouteComponent,
});

function RouteComponent() {
    return (
        <PageController route={Route}>
            <VehiclesTab />
        </PageController>
    );
}

type VehicleQueryParams = RouterInputs['fleetClientDb']['listFleetVehicles'];
type VehicleWhere = NonNullable<VehicleQueryParams['where']>;
type VehicleRow = NonNullable<RouterOutputs['fleetClientDb']['listFleetVehicles']>['rows'][number];
const columnHelper = createColumnHelper<VehicleRow, NonNullable<VehicleQueryParams['orderBy']>>();

function searchWhere(value: string): Pick<VehicleWhere, 'OR'> {
    const term = value.trim();
    if (!term) {
        return { OR: undefined };
    }
    return {
        OR: [
            { registration: { contains: term, mode: 'insensitive' } },
            { make: { contains: term, mode: 'insensitive' } },
            { PrimaryDriver: { is: { name: { contains: term, mode: 'insensitive' } } } },
        ],
    };
}

function VehiclesTab() {
    const { id } = Route.useParams();
    const { data: client } = useClientRecord(id);
    const canVerify = useCanManageFleetClients();
    const { formatDate } = useFormatter();
    const [params, setParams] = useState<VehicleQueryParams>({
        limit: 20,
        orderBy: { registration: 'asc' },
        where: {},
    });
    const [searchText, setSearchText] = useState('');
    const [selectedVehicleId, setSelectedVehicleId] = useState<bigint | null>(null);

    const isClient = client ? !isConsolidatorRecord(client) : false;
    const vehiclesQuery = trpc.fleetClientDb.listFleetVehicles.useQuery(
        isClient ? { ...params, where: { ...params.where, clientId: id } } : skipToken,
    );
    const optionsQuery = trpc.fleetClientDb.listFleetVehicleFilterOptions.useQuery(
        isClient ? { clientId: id } : skipToken,
        { meta: { errorSurface: 'inline' } },
    );

    const setWhere = (patch: Partial<VehicleWhere>) =>
        setParams((prev) => ({ ...prev, offset: 0, where: { ...prev.where, ...patch } }));

    const tableProps = usePrismaTable({
        rowKey: 'id',
        data: vehiclesQuery.data?.rows || [],
        total: vehiclesQuery.data?.total || 0,
        loading: vehiclesQuery.isLoading,
        queryParams: params,
        onParamsChange: setParams,
        columns: [
            columnHelper.accessor('registration', {
                id: 'registration',
                header: 'Rego',
                sortable: 'registration',
                width: 120,
                cell: ({ row }) => (
                    <div className="flex flex-col">
                        <span className="font-semibold tabular-nums">{row.registration}</span>
                        {row.stateOfRegistration ? (
                            <span className="text-subtle-foreground text-xs">{row.stateOfRegistration}</span>
                        ) : null}
                    </div>
                ),
            }),
            columnHelper.accessor('make', {
                id: 'vehicle',
                header: 'Vehicle',
                sortable: 'make',
                cell: ({ row }) => {
                    const detail = [row.description, row.colour].filter(Boolean).join(' · ');
                    return (
                        <div className="flex flex-col">
                            <span className="font-medium">
                                {row.make || <EmptyValue>Make not recorded</EmptyValue>}
                            </span>
                            {detail ? <span className="text-subtle-foreground text-xs">{detail}</span> : null}
                        </div>
                    );
                },
            }),
            columnHelper.accessor('leaseType', {
                id: 'lease',
                header: 'Lease',
                sortable: 'leaseType',
                width: 170,
                cell: ({ row }) => (
                    <div className="flex flex-col">
                        <span>{row.leaseType || <EmptyValue />}</span>
                        {row.leaseEndDate ? (
                            <span className="text-subtle-foreground text-xs">Ends {formatDate(row.leaseEndDate)}</span>
                        ) : null}
                    </div>
                ),
            }),
            columnHelper.accessor('insurance', {
                id: 'insurance',
                header: 'Insurance',
                sortable: 'insurance',
                width: 140,
                cell: ({ row }) => (row.insurance ? <ValueChip>{row.insurance}</ValueChip> : <EmptyValue />),
            }),
            columnHelper.accessor((row) => row.PrimaryDriver?.name, {
                id: 'driver',
                header: 'Driver',
                cell: ({ row }) => row.PrimaryDriver?.name || <EmptyValue>No driver</EmptyValue>,
            }),
            columnHelper.accessor((row) => row.FleetCostCentre?.name, {
                id: 'costCentre',
                header: 'Cost centre',
                width: 140,
                cell: ({ row }) => row.FleetCostCentre?.name || <EmptyValue />,
            }),
            columnHelper.actions({
                width: 40,
                cell: ({ row }) => <Button.View onClick={() => setSelectedVehicleId(row.id)} />,
            }),
        ],
    });

    if (client && !isClient) {
        return null;
    }

    const activeCount = client?.vehiclesCount ?? 0;

    return (
        <>
            <RecordCard padded={false}>
                <div className="flex flex-wrap items-center gap-2 border-separator border-b px-4 py-3">
                    <Input.Search
                        placeholder="Search rego, make or driver"
                        allowClear
                        className="w-72"
                        value={searchText}
                        onChange={(event) => setSearchText(event.target.value)}
                        onSearch={(value) => setWhere(searchWhere(value))}
                    />
                    <Select
                        placeholder="Lease type"
                        allowClear
                        className="w-44"
                        value={typeof params.where?.leaseType === 'string' ? params.where.leaseType : undefined}
                        loading={optionsQuery.isFetching}
                        disabled={!!optionsQuery.error && !optionsQuery.data}
                        options={(optionsQuery.data?.leaseTypes ?? []).map((value) => ({ value, label: value }))}
                        onChange={(value: string | undefined) => setWhere({ leaseType: value })}
                    />
                    <Select
                        placeholder="Insurance"
                        allowClear
                        className="w-40"
                        value={typeof params.where?.insurance === 'string' ? params.where.insurance : undefined}
                        loading={optionsQuery.isFetching}
                        disabled={!!optionsQuery.error && !optionsQuery.data}
                        options={(optionsQuery.data?.insurances ?? []).map((value) => ({ value, label: value }))}
                        onChange={(value: string | undefined) => setWhere({ insurance: value })}
                    />
                    <span className="ml-auto text-subtle-foreground text-xs">
                        {activeCount} active {activeCount === 1 ? 'vehicle' : 'vehicles'}
                    </span>
                </div>
                {optionsQuery.error ? (
                    <div className="px-4 py-3">
                        <InlineQueryError
                            compact
                            title="Could not load vehicle filter options"
                            description="Lease type and insurance options are unavailable. You can still search and view vehicles."
                            error={optionsQuery.error}
                            onRetry={() => void optionsQuery.refetch()}
                            retrying={optionsQuery.isFetching}
                        />
                    </div>
                ) : null}
                {vehiclesQuery.error ? (
                    <div className="p-4">
                        <InlineQueryError
                            compact
                            error={vehiclesQuery.error}
                            onRetry={() => void vehiclesQuery.refetch()}
                        />
                    </div>
                ) : (
                    <PrismaTable
                        {...tableProps}
                        onRow={({ id: vehicleId }) => ({
                            className: 'cursor-pointer',
                            onClick() {
                                setSelectedVehicleId(vehicleId);
                            },
                        })}
                    />
                )}
            </RecordCard>

            <VehicleDetailsDrawer
                vehicleId={selectedVehicleId}
                canVerify={canVerify}
                onClose={() => setSelectedVehicleId(null)}
            />
        </>
    );
}
